import { forwardRef, useCallback, useMemo, useState, type KeyboardEvent, type RefObject } from 'react';

// --- UI Primitives ---
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Input } from '@/components/tiptap-editor/tiptap-ui-primitive/input';

// --- Lib ---
import { cn } from '@/lib/tiptap-utils';

// --- Icons ---
import { TableColumnIcon } from '@/components/tiptap-editor/tiptap-icons/table-column-icon';
import { TableRowIcon } from '@/components/tiptap-editor/tiptap-icons/table-row-icon';

import type { TableDimensions } from './use-table-trigger';

import './table-grid-selector.scss';

// --- Types ---
export interface CellCoordinates {
    row: number;
    col: number;
}

export interface TableGridSelectorProps {
    /**
     * Initial number of rows to display in the grid
     * @default 8
     */
    maxRows?: number;
    /**
     * Initial number of columns to display in the grid
     * @default 8
     */
    maxCols?: number;
    /**
     * Selected table dimensions
     */
    dimensions: TableDimensions;
    /**
     * Callback when dimensions change via grid hover or number inputs
     */
    onDimensionsChange: (dimensions: TableDimensions) => void;
    /**
     * Insert using the current dimensions (Enter key or explicit action)
     */
    onInsert: () => void;
    /**
     * Currently hovered cell coordinates derived from dimensions
     */
    hoveredCell: CellCoordinates | null;
    /**
     * Callback when a cell is hovered
     */
    onCellHover: (row: number, col: number) => void;
    /**
     * Callback when a cell is clicked
     */
    onCellClick: (row: number, col: number) => void;
    /**
     * Ref for the columns number input
     */
    columnsInputRef?: RefObject<HTMLInputElement | null>;
    /**
     * Ref for the rows number input
     */
    rowsInputRef?: RefObject<HTMLInputElement | null>;
    /**
     * Whether the grid cells should be disabled
     * @default false
     */
    disabled?: boolean;
    /**
     * Additional class name for the container
     */
    className?: string;
    /**
     * Whether to show the size indicator inputs
     * @default true
     */
    showSizeIndicator?: boolean;
}

interface GridCellProps {
    row: number;
    col: number;
    isSelected: boolean;
    disabled: boolean;
    onMouseEnter: () => void;
    onClick: () => void;
}

const isCellSelected = (cell: CellCoordinates, hoveredCell: CellCoordinates | null): boolean => {
    if (!hoveredCell) return false;
    return cell.row <= hoveredCell.row && cell.col <= hoveredCell.col;
};

const generateGridCells = (rows: number, cols: number): CellCoordinates[] => {
    const totalCells = rows * cols;
    return Array.from({ length: totalCells }, (_, index) => ({
        row: Math.floor(index / cols),
        col: index % cols,
    }));
};

const GridCell = ({ row, col, isSelected, disabled, onMouseEnter, onClick }: GridCellProps) => (
    <Button
        size="small"
        type="button"
        className={cn('tiptap-table-grid-cell', isSelected && 'selected')}
        disabled={disabled}
        onMouseEnter={onMouseEnter}
        onClick={onClick}
        aria-label={`Select ${row + 1}x${col + 1} table`}
    />
);

function clampDimension(value: number, max: number): number {
    return Math.min(Math.max(Math.trunc(value), 1), max);
}

function parseCommittedDimension(raw: string, max: number): number {
    const parsed = Number.parseInt(raw, 10);
    return clampDimension(Number.isFinite(parsed) ? parsed : 1, max);
}

function sanitizeDimensionDraft(raw: string, max: number): string {
    const digits = raw.replace(/\D/g, '');
    const maxLength = String(max).length;
    return maxLength === 1 ? digits.slice(-1) : digits.slice(0, maxLength);
}

interface DimensionInputProps {
    value: number;
    max: number;
    disabled: boolean;
    inputRef?: RefObject<HTMLInputElement | null>;
    className?: string;
    ariaLabel: string;
    onChange: (value: number) => void;
    onEnter: () => void;
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>, commit: () => number) => void;
}

function DimensionInput({ value, max, disabled, inputRef, className, ariaLabel, onChange, onEnter, onKeyDown }: DimensionInputProps) {
    const [draft, setDraft] = useState(String(value));
    const [isFocused, setIsFocused] = useState(false);
    const displayValue = isFocused ? draft : String(value);

    const commit = useCallback(() => {
        const next = parseCommittedDimension(draft, max);
        onChange(next);
        setDraft(String(next));
        return next;
    }, [draft, max, onChange]);

    const handleDraftChange = useCallback(
        (raw: string) => {
            const nextDraft = sanitizeDimensionDraft(raw, max);
            setDraft(nextDraft);

            if (nextDraft === '') {
                return;
            }

            const parsed = Number.parseInt(nextDraft, 10);
            if (Number.isFinite(parsed)) {
                onChange(clampDimension(parsed, max));
            }
        },
        [max, onChange],
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                commit();
                onEnter();
                return;
            }

            if (event.key === 'Tab') {
                commit();
            }

            onKeyDown?.(event, commit);
        },
        [commit, onEnter, onKeyDown],
    );

    return (
        <Input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={displayValue}
            disabled={disabled}
            className={className}
            aria-label={ariaLabel}
            onChange={event => handleDraftChange(event.target.value)}
            onFocus={() => {
                setDraft(String(value));
                setIsFocused(true);
            }}
            onBlur={() => {
                setIsFocused(false);
                commit();
            }}
            onKeyDown={handleKeyDown}
        />
    );
}

interface DimensionInputsProps {
    dimensions: TableDimensions;
    maxRows: number;
    maxCols: number;
    disabled: boolean;
    columnsInputRef?: RefObject<HTMLInputElement | null>;
    rowsInputRef?: RefObject<HTMLInputElement | null>;
    onDimensionsChange: (dimensions: TableDimensions) => void;
    onInsert: () => void;
}

const DimensionInputs = ({
    dimensions,
    maxRows,
    maxCols,
    disabled,
    columnsInputRef,
    rowsInputRef,
    onDimensionsChange,
    onInsert,
}: DimensionInputsProps) => {
    const handleColumnsKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>, commit: () => number) => {
            if (event.key === 'Tab' && !event.shiftKey) {
                event.preventDefault();
                commit();
                rowsInputRef?.current?.focus();
                rowsInputRef?.current?.select();
            }
        },
        [rowsInputRef],
    );

    const handleRowsKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>, commit: () => number) => {
            if (event.key === 'Tab' && event.shiftKey) {
                event.preventDefault();
                commit();
                columnsInputRef?.current?.focus();
                columnsInputRef?.current?.select();
            }
        },
        [columnsInputRef],
    );

    return (
        <div className="tiptap-table-size-indicator">
            <label className="tiptap-table-size-indicator-item">
                <TableColumnIcon className="tiptap-table-column-icon" />
                <DimensionInput
                    value={dimensions.cols}
                    max={maxCols}
                    disabled={disabled}
                    inputRef={columnsInputRef}
                    className="tiptap-table-size-indicator-input"
                    ariaLabel="Columns"
                    onChange={cols =>
                        onDimensionsChange({
                            ...dimensions,
                            cols,
                        })
                    }
                    onEnter={onInsert}
                    onKeyDown={handleColumnsKeyDown}
                />
            </label>
            <span className="tiptap-table-size-indicator-delimiter">x</span>
            <label className="tiptap-table-size-indicator-item">
                <TableRowIcon className="tiptap-table-row-icon" />
                <DimensionInput
                    value={dimensions.rows}
                    max={maxRows}
                    disabled={disabled}
                    inputRef={rowsInputRef}
                    className="tiptap-table-size-indicator-input"
                    ariaLabel="Rows"
                    onChange={rows =>
                        onDimensionsChange({
                            ...dimensions,
                            rows,
                        })
                    }
                    onEnter={onInsert}
                    onKeyDown={handleRowsKeyDown}
                />
            </label>
        </div>
    );
};

/**
 * Reusable table grid selector component for selecting table dimensions.
 *
 * @example
 * ```tsx
 * const [hoveredCell, setHoveredCell] = useState<CellCoordinates | null>(null)
 *
 * <TableGridSelector
 *   maxRows={8}
 *   maxCols={8}
 *   hoveredCell={hoveredCell}
 *   onCellHover={(row, col) => setHoveredCell({ row, col })}
 *   onCellClick={(row, col) => insertTable(row + 1, col + 1)}
 *   onMouseLeave={() => setHoveredCell(null)}
 * />
 * ```
 */
export const TableGridSelector = forwardRef<HTMLDivElement, TableGridSelectorProps>(
    (
        {
            maxRows = 8,
            maxCols = 8,
            dimensions,
            onDimensionsChange,
            onInsert,
            hoveredCell,
            onCellHover,
            onCellClick,
            columnsInputRef,
            rowsInputRef,
            disabled = false,
            className,
            showSizeIndicator = true,
        },
        ref,
    ) => {
        const gridCells = useMemo(() => generateGridCells(maxRows, maxCols), [maxRows, maxCols]);

        const blurDimensionInputs = useCallback(() => {
            columnsInputRef?.current?.blur();
            rowsInputRef?.current?.blur();
        }, [columnsInputRef, rowsInputRef]);

        const gridStyle = useMemo(
            () =>
                ({
                    '--tt-table-columns': maxCols,
                    '--tt-table-rows': maxRows,
                }) as React.CSSProperties,
            [maxCols, maxRows],
        );

        const handleCellHover = useCallback(
            (row: number, col: number) => () => {
                blurDimensionInputs();
                onCellHover(row, col);
            },
            [blurDimensionInputs, onCellHover],
        );

        const handleCellClick = useCallback(
            (row: number, col: number) => () => {
                blurDimensionInputs();
                onCellClick(row, col);
            },
            [blurDimensionInputs, onCellClick],
        );

        return (
            <>
                {showSizeIndicator && (
                    <DimensionInputs
                        dimensions={dimensions}
                        maxRows={maxRows}
                        maxCols={maxCols}
                        disabled={disabled}
                        columnsInputRef={columnsInputRef}
                        rowsInputRef={rowsInputRef}
                        onDimensionsChange={onDimensionsChange}
                        onInsert={onInsert}
                    />
                )}

                <div
                    ref={ref}
                    className={cn('tiptap-table-grid', className)}
                    style={gridStyle}
                >
                    {gridCells.map((cell, index) => (
                        <GridCell
                            key={index}
                            row={cell.row}
                            col={cell.col}
                            isSelected={isCellSelected(cell, hoveredCell)}
                            disabled={disabled}
                            onMouseEnter={handleCellHover(cell.row, cell.col)}
                            onClick={handleCellClick(cell.row, cell.col)}
                        />
                    ))}
                </div>
            </>
        );
    },
);

TableGridSelector.displayName = 'TableGridSelector';
